import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Pencil, Plus, Tag, Trash } from "lucide-react";

// UI Components
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// Custom Components
import ConfirmationModal from "../Modal/ConfirmationModal";
import FeaturePartModal from "../Modal/FeaturePartModal";

// Store Actions
import {
  deletePart,
  fetchFeaturedInventory,
  fetchSellerInventory
} from "../../store/useParts/usePartsThunk";

const Spinner = () => (
  <div className="flex justify-center items-center py-3">
    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-500"></div>
  </div>
);

// Constants
const TAB_VALUES = {
  ALL: "all",
  FEATURED: "featured"
};

const STATUS_VARIANTS = {
  Active: "success",
  "Low Stock": "warning",
  "Out of Stock": "destructive"
};

// Helper functions
const getStatusVariant = (status) => {
  return STATUS_VARIANTS[status] || "secondary";
};

const getDisplayName = (item) => {
  if (!item) return "";

  const make =
    item?.make?.toLowerCase() === "other" ? item?.sub_make : item?.make;
  const model =
    item?.model?.toLowerCase() === "other" ? item?.sub_model : item?.model;
  const year = item?.year;
  const name =
    item?.name?.toLowerCase() === "other" ? item?.sub_name : item?.name;

  return [make, model, year, name].filter(Boolean).join(" ");
};

const formatPrice = (price) => {
  return typeof price === "number" ? `$${price.toFixed(2)}` : "N/A";
};

// Check if an item is featured
const isItemFeatured = (itemId, featuredInventory) => {
  return featuredInventory.some((featured) => featured.id === itemId);
};

// Table Components
const AllPartsTable = ({
  inventory,
  featuredInventory,
  hasMore,
  paginationLoading,
  onLoadMore,
  onEdit,
  onDelete
}) => {
  return (
    <ScrollArea className="h-[300px] border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[5%] text-center whitespace-nowrap">
              #
            </TableHead>
            <TableHead className="w-[35%] whitespace-nowrap">
              Item Name
            </TableHead>
            <TableHead className="text-center whitespace-nowrap">
              Quantity
            </TableHead>
            <TableHead className="text-right whitespace-nowrap">
              Price
            </TableHead>
            <TableHead className="text-center whitespace-nowrap">
              Status
            </TableHead>
            <TableHead className="text-center whitespace-nowrap">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {inventory.length > 0 ? (
            inventory.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className="text-center">{index + 1}</TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <span className="truncate" title={getDisplayName(item)}>
                      {getDisplayName(item)}
                    </span>
                    {isItemFeatured(item.id, featuredInventory) && (
                      <Badge
                        variant="outline"
                        className="flex items-center gap-1 text-[11px] font-medium border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors rounded-md px-2 py-0.5"
                      >
                        <Tag className="w-3.5 h-3.5 text-amber-600" />
                        <span>Featured</span>
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-center">{item.quantity}</TableCell>
                <TableCell className="text-right">
                  {formatPrice(item.price)}
                </TableCell>
                <TableCell className="text-center whitespace-nowrap">
                  <Badge
                    variant={getStatusVariant(item.status)}
                    className="text-xs"
                  >
                    {item.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Pencil
                      className="w-4 h-4 cursor-pointer text-blue-500 hover:text-blue-700"
                      onClick={() => onEdit(item)}
                    />
                    <Trash
                      className="w-4 h-4 cursor-pointer text-red-500 hover:text-red-700"
                      onClick={() => onDelete(item)}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={6}
                className="text-center text-muted-foreground h-24"
              >
                No inventory items found. Add parts to see them here.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {hasMore && inventory.length > 0 && (
        <div className="flex justify-center py-3">
          <Button
            size="sm"
            variant="outline"
            onClick={onLoadMore}
            disabled={paginationLoading}
          >
            {paginationLoading ? <Spinner /> : "Load More"}
          </Button>
        </div>
      )}

      {!hasMore && inventory.length > 0 && (
        <div className="text-center py-4 text-sm text-muted-foreground">
          All items loaded ({inventory.length} total)
        </div>
      )}
    </ScrollArea>
  );
};

const FeaturedPartsTable = ({
  featuredInventory,
  featuredHasMore,
  featuredPaginationLoading,
  onLoadMore
}) => {
  return (
    <ScrollArea className="h-[300px] border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[5%] text-center whitespace-nowrap">
              #
            </TableHead>
            <TableHead className="w-[40%] whitespace-nowrap">
              Item Name
            </TableHead>
            <TableHead className="text-center whitespace-nowrap">
              Quantity
            </TableHead>
            <TableHead className="text-right whitespace-nowrap">
              Price
            </TableHead>
            <TableHead className="text-center whitespace-nowrap">
              Status
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {featuredInventory.length > 0 ? (
            featuredInventory.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className="text-center">{index + 1}</TableCell>
                <TableCell
                  className="font-medium truncate"
                  title={getDisplayName(item)}
                >
                  {getDisplayName(item)}
                </TableCell>
                <TableCell className="text-center">{item.quantity}</TableCell>
                <TableCell className="text-right">
                  {formatPrice(item.price)}
                </TableCell>
                <TableCell className="text-center whitespace-nowrap">
                  <Badge
                    variant={getStatusVariant(item.status)}
                    className="text-xs"
                  >
                    {item.status}
                  </Badge>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={5}
                className="text-center text-muted-foreground h-24"
              >
                No featured items found. Add featured parts to see them here.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {featuredHasMore && featuredInventory.length > 0 && (
        <div className="flex justify-center py-3">
          <Button
            size="sm"
            variant="outline"
            onClick={onLoadMore}
            disabled={featuredPaginationLoading}
          >
            {featuredPaginationLoading ? <Spinner /> : "Load More"}
          </Button>
        </div>
      )}

      {!featuredHasMore && featuredInventory.length > 0 && (
        <div className="text-center py-4 text-sm text-muted-foreground">
          All featured items loaded ({featuredInventory.length} total)
        </div>
      )}
    </ScrollArea>
  );
};

const InventoryOverview = ({ onRefreshInventory }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Selectors
  const {
    deleteLoading,
    // All inventory
    inventory,
    hasMore,
    paginationLoading,
    pageSize,
    // Featured inventory
    featuredInventory,
    featuredHasMore,
    featuredPaginationLoading
  } = useSelector((state) => state.useParts);

  const { user } = useSelector((state) => state.auth);

  // State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [isFeaturePartModalOpen, setIsFeaturePartModalOpen] = useState(false);

  // Animation variants
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  // Handlers
  const loadMoreInventory = useCallback(() => {
    if (paginationLoading || !hasMore || !user?.uid) return;
    dispatch(
      fetchSellerInventory({
        sellerId: user.uid,
        loadMore: true,
        pageSize
      })
    );
  }, [dispatch, paginationLoading, hasMore, user?.uid, pageSize]);

  const loadMoreFeaturedInventory = useCallback(() => {
    if (featuredPaginationLoading || !featuredHasMore || !user?.uid) return;
    dispatch(
      fetchFeaturedInventory({
        sellerId: user.uid,
        loadMore: true,
        pageSize
      })
    );
  }, [
    dispatch,
    featuredPaginationLoading,
    featuredHasMore,
    user?.uid,
    pageSize
  ]);

  const handleEdit = (item) => {
    navigate(`/dashboard/add-part?item=${item?.id}`);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const confirmDelete = () => {
    if (selectedItem?.id) {
      dispatch(deletePart(selectedItem.id));
      setTimeout(() => {
        onRefreshInventory();
        setIsModalOpen(false);
        setSelectedItem(null);
      }, 1000);
    }
  };

  const handleAddToFeatured = () => {
    setIsFeaturePartModalOpen(true);
  };

  const handleFeaturedPartSuccess = async () => {
    await onRefreshInventory();
    setIsFeaturePartModalOpen(false);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const handleFeatureModalClose = () => {
    setIsFeaturePartModalOpen(false);
  };

  return (
    <motion.div variants={itemVariants}>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="w-full sm:w-auto">
              <CardTitle>Inventory Overview</CardTitle>
              <CardDescription className="mt-3">
                A quick look at your current stock levels.
              </CardDescription>
            </div>
            {activeTab === TAB_VALUES.FEATURED && (
              <Button
                onClick={handleAddToFeatured}
                size="sm"
                className="w-full sm:w-auto gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Part to Featured
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value={TAB_VALUES.ALL}>All Parts</TabsTrigger>
              <TabsTrigger value={TAB_VALUES.FEATURED}>
                Featured Parts
              </TabsTrigger>
            </TabsList>

            {/* ALL PARTS */}
            <TabsContent value={TAB_VALUES.ALL}>
              <AllPartsTable
                inventory={inventory}
                featuredInventory={featuredInventory}
                hasMore={hasMore}
                paginationLoading={paginationLoading}
                onLoadMore={loadMoreInventory}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
              />
            </TabsContent>

            {/* FEATURED PARTS */}
            <TabsContent value={TAB_VALUES.FEATURED}>
              <FeaturedPartsTable
                featuredInventory={featuredInventory}
                featuredHasMore={featuredHasMore}
                featuredPaginationLoading={featuredPaginationLoading}
                onLoadMore={loadMoreFeaturedInventory}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onConfirm={confirmDelete}
        title={`Are you sure you want to delete "${
          selectedItem?.name == "other"
            ? selectedItem?.sub_name
            : selectedItem?.name
        }"?`}
        confirmText="Confirm"
        cancelText="Cancel"
        loading={deleteLoading}
      />

      <FeaturePartModal
        isOpen={isFeaturePartModalOpen}
        onClose={handleFeatureModalClose}
        onSuccess={handleFeaturedPartSuccess}
      />
    </motion.div>
  );
};

export default InventoryOverview;
